import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMobileDetection } from '../../hooks/use-mobile-detection';

interface MobileRedirectProps {
  /**
   * Whether to force redirects regardless of current state
   * Useful when testing
   */
  forceRedirect?: boolean;
}

/**
 * Component that redirects users to mobile routes if they're on a mobile device
 * and aren't already on a mobile route.
 */
export function MobileRedirect({ forceRedirect = false }: MobileRedirectProps) {
  const isMobile = useMobileDetection();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only redirect if on mobile and not already on a mobile route
    if (
      (isMobile || forceRedirect) &&
      !location.pathname.startsWith('/mobile')
    ) {
      // Don't redirect from login page
      if (location.pathname === '/login') {
        return;
      }

      // Handle special cases for different routes
      if (location.pathname === '/' || location.pathname === '/projects') {
        navigate('/mobile', { replace: true });
        return;
      }

      // For project-specific routes
      const projectMatch = location.pathname.match(/\/project\/([^/]+)/);
      if (projectMatch) {
        const projectId = projectMatch[1];

        // Check for order-specific routes
        const orderMatch = location.pathname.match(
          /\/project\/[^/]+\/takeoff\/([^/]+)/
        );
        if (orderMatch) {
          const orderId = orderMatch[1];
          navigate(`/mobile/project/${projectId}/table/${orderId}`, {
            replace: true,
          });
          return;
        }

        // If just project route
        navigate(`/mobile/project/${projectId}`, { replace: true });
        return;
      }

      // For any other routes, default to mobile homepage
      navigate('/mobile', { replace: true });

      // TODO: Support initial states
    }
  }, [isMobile, location.pathname, navigate, forceRedirect]);

  // This component doesn't render anything
  return null;
}
